import React, { useState, useEffect, useCallback } from 'react';
import sendData from 'redux/actions/sendData';
import { first, get, set, camelCase, uniqBy, debounce, unset } from 'global/lib/lodash';
import useRetrieveData from 'global/hooks/useRetrieveData';
import { useSupersets } from 'global/hooks/useSupersets';
import { _size, cleanCopy, findOne } from '../../utils';
import { uploadImageToPerform } from '../../../redux/actions/imageUploader';
import { isLocal } from './index';

export const createKeyBindings = (opts = {}) => {
  const keydown = function(event) {
    event.preventDefault();
    const ename = `on${event.key}`;
    opts[ename] && opts[ename](event);
  }

  const wheel = function(e) {
    opts.onScroll && opts.onScroll(e);
  };

  const mouseevent = function(_name, e) {
    const name = `on_${_name}`;
    opts[name] && opts[name](e);
  }

  const windows = {
    document: document,
    window: window,
  }
  
  const listeners = [
    { name: 'keyup', func: keydown, window: windows.document },
    { name: 'wheel', func: wheel, window: opts.element || windows.window },
    { name: 'mousemove', func: mouseevent.bind(this, 'mousemove'), window: opts.element || windows.document },
    { name: 'mousedown', func: mouseevent.bind(this, 'mousedown'), window: opts.element || windows.document },
    { name: 'mouseup', func: mouseevent.bind(this, 'mouseup'), window: opts.element || windows.document },
    { name: 'mouseleave', func: mouseevent.bind(this, 'mouseleave'), window: opts.element || windows.document },
  ]

  listeners.map(l => l.window.addEventListener(l.name, l.func));

  return () => {
    listeners.map(l => l.window.removeEventListener(l.name, l.func));
  }
}

export const isHighlight = (collectionName) => {
  return collectionName === 'highlights' || collectionName === 'report-section-bp-images' || collectionName === 'report-section-site-images';
};

const getTypeFromCollectionName = cn => {
  let name = cn;
  if (cn == 'realm-images') {
    name = 'realm-photos'
  } else if (cn == 'scorecard-score-images') {
    name = 'scorecard-scores'
  } else if (cn == 'sub-score-images') {
    name = 'sub-scores'
  }

  return name;
}

const DefaultValues = {
  quality: null,
  supersets: [],
  location: null,
  custom_properties: {
    caption: '',
  }
};

export const useEditData = (props) => {
  const [state, setState] = useState({
    loading: true,
    updating: false,
    hasEdits: false,
    mediaChanges: {},
  })
  const [values, _setValues] = useState(cleanCopy(DefaultValues));

  const imagetype = get(props, 'imagetype', 'realm-photos');
  const parentId = get(props, 'parentId'); 
  const imageid = get(props, 'id');

  //load the assessment
  const assessment = get(props, 'assessment.data', get(props, 'assessment'));
  const aid = get(assessment, 'id', null);
  const adata = useRetrieveData(
    { url: `/assessments/${aid}` },
    {
      waitFor: aid,
      setLoadingEvenIfWait: true,
      useEffect: true,
      build: ['assessments'],
      useCache: { reload: 2 },
      overrideNotFound: true,
      params: {
        include: 'realm,realm.realms,authors',
      },
      transformData: (d) => {
        const ret = first(d);

        const locations = uniqBy(
          [
            get(ret, 'realm'),
            ...get(ret, 'realm.realms', []).sort((a, b) => {
              const A = get(a, 'name');
              const B = get(b, 'name');
              return A === B ? 0 : A > B ? 1 : -1;
            }),
          ],
          'id'
        );

        return {
          ...ret,
          locations,
        };
      },
    }
  );

  //load supersets
  const { supersets } = useSupersets({ assessment });

  //the parent collection data
  const imgdata = useDataRetriever(props);

  //the actual media data
  const local = isLocal(props);
  const media_id = (() => {
    if (local) { return null }
    const alwaysUseImageId = get(props, 'alwaysUseImageId', false);
    const mid = get(props, 'mediaId', alwaysUseImageId || imgdata.collectionName.includes('realm-') ? imageid : get(imgdata, 'data.images.0.id', get(props, 'id', imageid)));
    return get(state, `mediaChanges.${mid}`, mid);
  })();

  const mediadata = useRetrieveData({ url: `/media/${media_id}` }, {
    waitFor: media_id,
    useEffect: true,
    build: ['media'],
    transformData: d => first(d),
  })

  //actually update the media properties
  const updateMediaProperties = useCallback(async (vals) => {
    try {
      await sendData({ 
        url: `/media/${media_id}`,
        method: 'patch',
        data: {
          data: {
            type: 'media',
            id: `${media_id}`,
            attributes: {
              custom_properties: vals.custom_properties
            }
          }
        }
      })
    } catch (err) {
      console.error(err);
    }
  }, [media_id]);

  //send requests to update image values
  const updateValues = useCallback(debounce(async (vals = {}) => {
    try {
      const id = parentId || imageid;
      if (!id) { return }

      props.onValuesChange && props.onValuesChange({
        id: media_id,
        imagetype,
        values: vals,
      })

      if (local) { return }

      setState(s => ({ ...s, updating: true }));

      let resp;
      if (imagetype !== 'media') {
        const data = {
          data: {
            type: imagetype,
            id: `${id}`,
            attributes: {
              name: vals.name || get(props, 'name'),
              ...vals,
              realm_id: get(vals, 'location'),
              supersets: undefined,
              location: undefined,
            },
            relationships: {
              supersets: {
                data: vals.supersets.map(s => ({ id: `${s}`, type: 'supersets' }))
              }
            }
          }
        }

        resp = await sendData(
          {
            url: `/${imagetype}/${id}`,
            method: 'patch',
            data,
          },
          { normalize: { build: [camelCase(imagetype)] } }
        ).catch(console.error);
      }
      
      await updateMediaProperties(vals);

      return first(resp);
    } catch (err) {
      console.error(err);
    } finally {
      setState(s => ({ ...s, updating: false }));
    }
  }, 1000), [media_id, imageid, imgdata.data]); 

  const updateMediaImageId = img => {
    setState(s => {
      const mediaChanges = { ...s.mediaChanges };
      if (mediaChanges[img.id]) { //already exists we are likely restoring original
        unset(mediaChanges, img.id);
        mediadata.update();
      } else {
        mediaChanges[media_id] = img.id;
      }
      return { 
        ...s, 
        mediaChanges,
      }
    })
  }

  const updateImageDataRef = useCallback(async(img) => {
    try {
      const isDel = img == null;
      const collectionName = get(imgdata, 'collectionName');
      const type = getTypeFromCollectionName(collectionName);

      // For library checklist/checkpoint media, the Media dialog is opened from a
      // form (DocumentChecklist EDIT MEDIA) that owns the image array and persists
      // changes itself on submit via PATCH /checkpoints/{id} or PATCH /checklists/{id}.
      // We don't have a parentId here for those collections, and there is no direct
      // REST resource at `/checkpoint-images/{id}` or `/checklist-images/{id}`, so
      // skip the API call and let the parent form persist via onValuesChange.
      // (PFM-2577)
      const isFormManagedNoParent =
        props.childImagesIncludedInEdit && !parentId &&
        (collectionName === 'checkpoint-images' || collectionName === 'checklist-images');

      if (isFormManagedNoParent) {
        if (!isDel) {
          updateMediaImageId(img);
        }

        setState(s => ({ ...s, hasEdits: true }));

        props.onValuesChange && props.onValuesChange({
          id: media_id,
          imagetype,
          values,
          archived: isDel,
        });

        return;
      }

      const id = parentId || imageid;

      const data = {
        data: {
          type: type,
          id: `${id}`,
          attributes: isDel && type == 'realm-photos' ? { archived: true } : undefined,
        }
      }

      let images = isDel ? [] : [img];
      if (props.childImagesIncludedInEdit) {
        images = [...props.images.filter(im => im.id != media_id), ...images];
      }

      set(data, 'data.relationships.images.data', images.map(im => ({ type: 'media', id: `${im.id}` })))

      const resp = await sendData(
        {
          url: `/${type}/${id}`,
          method: 'patch',
          data,
        },
        { normalize: { build: [camelCase(type)] } }
      ).catch(e => {
        if (e.toString().includes('does not exist')) {
          return {};
        }
        throw e;
      });

      if (!isDel) {
        updateMediaImageId(img);
      }

      setState(s => ({ ...s, hasEdits: true }));

      props.onValuesChange && props.onValuesChange({
        id: media_id,
        imagetype,
        values,
        archived: isDel,
      })

      return get(resp, 'data.data');
    } catch (err) {
      console.error(err);
    }
  }, [imageid, media_id, imgdata.data]);

  const updateImage = useCallback(async (vals = {}) => {
    try {
      setState(s => ({ ...s, updating: true }));

      const img = { 
        ...vals,
        file: vals.imageBase64, 
        name: get(mediadata, 'data.fileName', vals.fullName),
        collection: get(props, 'collection', get(mediadata, 'data.collectionName', get(imgdata, 'collectionName'))),
        custom_properties: {
          ...(values.custom_properties || {}),
          annotation: true,
          originalId: get(mediadata, 'data.custom_properties.originalId', media_id),
        },
      };

      const resp = await uploadImageToPerform(img);

      if (props.uploadAllNew) {
        updateMediaImageId(resp);
        props.onValuesChange && props.onValuesChange({
          id: media_id,
          imagetype,
          values,
          newimg: resp,
        })
        return;
      }

      await updateImageDataRef(resp);
    } catch (err) {
      console.error(err);
    } finally {
      setState(s => ({ ...s, updating: false }))
    }
  }, [values, imageid, imgdata.data, mediadata.data]);

  const getOriginalImage = useCallback(async () => {
    try {
      const orig = get(mediadata, 'data.customProperties.originalId');

      if (orig) {
        const resp = await sendData({ url: `/media/${orig}` }, {
          normalize: { build: ['media'] }
        })

        return first(resp);
      }
    } catch (err) {
      console.error(err)    ;
    }
  }, [media_id, mediadata]);

  const setOriginalImage = useCallback(async (opts = {}) => {
    opts = {
      confirm: true,
      ...opts,
    }

    const goahead = opts.confirm ? window.confirm('Are you sure? This cannot be undone.') : true;

    if (goahead) {
      const orig = await getOriginalImage();
  
      await sendData({ 
        url: `/media/${orig.id}`,
        method: 'patch',
        data: {
          data: {
            type: 'media',
            id: `${orig.id}`,
            attributes: {
              custom_properties: {
                ...(values.custom_properties || {}),
                annotation: undefined,
                originalId: undefined,
              }
            }
          }
        }
      })

      if (props.uploadAllNew) {
        updateMediaImageId(orig);
        props.onValuesChange && props.onValuesChange({
          id: media_id,
          imagetype,
          values,
          newimg: orig,
        })
        return;
      }
  
      await updateImageDataRef(orig);
    }
  }, [values, media_id, mediadata])

  const deleteImage = useCallback(async (opts = {}) => {
    opts = {
      confirm: true,
      ...opts,
    }

    const goahead = opts.confirm ? window.confirm('Are you sure? This cannot be undone.') : true;

    if (goahead) {
      setState(s => ({ ...s, hasEdits: true }))

      await updateImageDataRef(null);
    }
  }, [values, media_id, mediadata])

  useEffect(() => {
    const isLoading = adata.loading || imgdata.loading;

    if (isLoading) {
      setState(s => ({ ...s, loading: isLoading }))
    } else {
      setTimeout(() => {
        setState(s => ({ ...s, loading: isLoading }))
      }, 50);
    }

  }, [adata.loading, imgdata.loading]);

  //set the original values
  useEffect(() => {
    if (imgdata.data && !imgdata.loading && !local) {
      _setValues((v) => ({
        ...v,
        quality: get(imgdata, 'data.quality', null),
        location: get(props, 'realm.id', get(imgdata, 'data.realm.id')),
        supersets: get(imgdata, 'data.supersets', []).map(ss => ss.id)
      }));
    }
  }, [imgdata.data, imgdata.loading]);

  //set the customproperties which actually exist on the media item
  useEffect(() => {
    if (local) {
      _setValues((v) => ({
        ...v,
        quality: get(props, 'quality', v.quality),
        supersets: get(props, 'supersets', v.supersets),
        location: get(props, 'realmId', v.location),
        custom_properties: {
          caption: get(props, 'caption', ''),
          ...get(props, 'custom_properties', {}),
        },
      }));
    } else if (mediadata.data && !mediadata.loading) {
      const cprops = {
        ...get(mediadata, 'data.customProperties', {}),
        ...get(props, 'custom_properties', {}),
      };
      _setValues((v) => ({
        ...v,
        custom_properties: {
          ...cprops,
          caption: get(cprops, 'caption') || get(props, 'caption', ''),
        },
      }));
    }

    setState(s => ({ ...s, loading: mediadata.loading }));
  }, [mediadata.data, mediadata.loading, local]);

  const setValues = func => {
    _setValues(vv => {
      const newv = func(vv);
      updateValues(newv);
      return newv;
    })
    setState(s => ({ ...s, hasEdits: true }));
  }

  return {
    ...state,
    updateValues,
    updateImage,
    getOriginalImage,
    setOriginalImage,
    deleteImage,

    mediadata: get(mediadata, 'data', {}),
    imgdata: get(imgdata, 'data', {}),
    assessment: get(adata, 'data', {}),
    realm: get(props, 'realm') || get(imgdata, 'data.realm') || get(adata, 'data.realm'),
    collectionName: get(imgdata, 'collectionName'),
    supersets,
    hasValidData: !!imageid,
    
    values,
    setValues,
  };
};

export const useDataRetriever = (props) => {
  const [params, setParams] = useState([{ url: '/' }, { waitFor: null, setLoadingEvenIfWait: true, }]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState({});

  const collectionName = get(props, 'collection', get(props, 'collectionName', get(props, 'collection_name', get(props, 'imagetype', 'realm-photos'))));
  const pid = findOne(props, ['parentId', 'scorecardScoreId', 'scorecard_score.id', 'task.id', 'report_section.id']) || props.id;
  const local = isLocal(props);

  const getMore = async (data, key) => {
    try {
      setLoading(true);

      if (key === 'processedScorecardScoreValues') {
        const resp = await sendData(
          {
            url: `/processed-scorecard-score-values`,
          },
          {
            normalize: {
              build: [key],
            },
            params: {
              filter: {
                scorecard_id: data.scorecardId,
                assessment_id: data.assessmentId,
              },
              include: 'superset,assessment',
            },
          }
        );
        const found = resp.find((f) => f.scorecardScoreId == pid);
        setData({ ...data, [key]: found });
      } else if (key === 'scorecards') {
        const resp = await sendData(
          {
            url: `/scorecards/${data.scorecardId}`,
          },
          {
            normalize: {
              build: [key],
            },
          }
        );
        setData({ ...data, [key]: first(resp) });
      }
    } catch (err) {
      console.error(err);
      setData(data);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (local) { return }

    let obj1 = {}, obj2 = { waitFor: null, setLoadingEvenIfWait: true, };

    if (collectionName === 'scorecard-score-images') {
      // const scid = get(props, 'parentId', get(props, 'scorecard_score.id', get(props, 'scorecardScoreId')));
      obj1 = { url: `/scorecard-scores/${pid}` };
      obj2 = { build: ['scorecardScores'], transformData: (d) => first(d) };
    } else if (collectionName === 'task-images' || collectionName === 'comment-images') {
      obj1 = { url: `/tasks/${pid}` };
      obj2 = {
        build: ['tasks'],
        transformData: (d) => first(d),
        overrideNotFound: true,
        params: {
          include: 'authors,images,realm',
        },
      };
    } else if (isHighlight(collectionName)) {
      obj1 = { url: `/report-sections/${pid}` };
      obj2 = {
        build: ['reportSections'],
        transformData: (d) => first(d),
        overrideNotFound: true,
        params: {
          include: 'user,assessment,images,superset',
        },
      };
    } else if (['realm-images', 'realm-photos'].includes(collectionName)) {
      obj1 = { url: `/realm-photos/${pid}` };
      obj2 = !pid ? obj2 : {
        build: ['realmPhotos'],
        transformData: (d) => first(d),
        overrideNotFound: true,
        params: {
          include: 'supersets,realm,images',
        },
      };
    }

    if (obj1 || obj2) {
      setParams([obj1, obj2]);
    }

    setData({});
  }, [collectionName, pid, local]);

  const ret = useRetrieveData(...params);

  useEffect(() => {
    if (ret.data && !ret.loading) {
      if (collectionName === 'scorecard-score-images' && _size(ret.data)) {
        getMore(ret.data, 'scorecards');
      } else {
        setData(ret.data)
      }
    }
  }, [ret.data, ret.loading, pid, collectionName]);

  return {
    ...ret,
    data,
    loading: ret.loading || loading,
    collectionName,
  };
};